import React from 'react';
import { Box, Stack } from '@mui/material';
import {
  getTierPrice,
  isTierFree,
  formatCurrency,
  getMonthlyCommitmentTotal,
  getBillingCycleSuffix,
  getTierBadge,
} from '../../service/membershipPricing';

// One tier tile in the selection grid. Price shown is ALWAYS re-derived
// from the tier + active billingCycle via getTierPrice — this is the fix
// for the old bug where the top tier showed the same number on both
// monthly and annual (see membershipPricing.js header comment).
// Clicking an already-selected tier deselects it (back to no tier / guest
// mode) rather than re-selecting the same one — lets a visitor back out
// of a choice without a separate "clear" control.
export default function TierCard({ tier, billingCycle, selected, onSelect }) {
  const handleActivate = () => onSelect(selected ? null : tier);
  const free = isTierFree(tier);
  const price = getTierPrice(tier, billingCycle);
  const commitmentTotal = billingCycle === 'monthly' ? getMonthlyCommitmentTotal(tier) : null;
  const badge = getTierBadge(tier);

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={handleActivate}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') handleActivate();
      }}
      aria-pressed={selected}
      sx={{
        position: 'relative',
        cursor: 'pointer',
        p: { xs: 3, md: 3.5 },
        border: '1px solid',
        borderColor: selected ? 'var(--navy-500)' : 'rgba(36,34,88,0.16)',
        background: selected ? 'var(--navy-500)' : 'var(--paper)',
        color: selected ? '#fff' : 'var(--navy-500)',
        transition: 'all 250ms ease',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        '&:hover': { borderColor: 'var(--navy-500)' },
      }}
    >
      {badge && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            transform: 'translateY(-50%)',
            px: 1.5,
            py: 0.5,
            background: 'var(--teal-500)',
            color: '#fff',
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            fontWeight: 700,
            letterSpacing: '0.14em',
          }}
        >
          {badge}
        </Box>
      )}

      <Box
        sx={{
          fontFamily: 'var(--font-mono)',
          fontSize: 10.5,
          letterSpacing: '0.2em',
          textTransform: 'uppercase',
          opacity: 0.75,
        }}
      >
        Membership
      </Box>

      <Box sx={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 22, lineHeight: 1.1 }}>
        {tier.name}
      </Box>

      <Stack direction="row" alignItems="baseline" spacing={1} sx={{ mt: 0.5 }}>
        <Box sx={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 34, lineHeight: 1 }}>
          {free ? 'Free' : formatCurrency(price)}
        </Box>
        {!free && (
          <Box sx={{ fontFamily: 'var(--font-mono)', fontSize: 11, letterSpacing: '0.08em', opacity: 0.8 }}>
            {getBillingCycleSuffix(billingCycle)}
          </Box>
        )}
      </Stack>

      {free && (
        <Box sx={{ fontFamily: 'var(--font-body)', fontSize: 13, opacity: 0.85 }}>
          No payment step required.
        </Box>
      )}

      {!free && billingCycle === 'monthly' && commitmentTotal != null && (
        <Box sx={{ fontFamily: 'var(--font-body)', fontSize: 12.5, opacity: 0.8 }}>
          12 payments · {formatCurrency(commitmentTotal)}/year total
        </Box>
      )}

      {tier.notes && (
        <Box sx={{ fontFamily: 'var(--font-body)', fontSize: 13, opacity: 0.75, mt: 0.5 }}>
          {tier.notes}
        </Box>
      )}

      {Array.isArray(tier.features) && tier.features.length > 0 && (
        <Stack component="ul" spacing={0.75} sx={{ listStyle: 'none', m: 0, p: 0, mt: 0.5 }}>
          {tier.features.map((feature) => (
            <Stack
              key={feature}
              component="li"
              direction="row"
              spacing={1}
              alignItems="flex-start"
              sx={{ fontFamily: 'var(--font-body)', fontSize: 13, lineHeight: 1.45, opacity: 0.9 }}
            >
              <Box component="span" aria-hidden sx={{ opacity: 0.7 }}>
                ·
              </Box>
              <Box component="span">{feature}</Box>
            </Stack>
          ))}
        </Stack>
      )}

      <Box
        sx={{
          mt: 'auto',
          pt: 2,
          fontFamily: 'var(--font-body)',
          fontWeight: 700,
          fontSize: 11.5,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: selected ? '#fff' : 'var(--teal-500)',
        }}
      >
        {selected ? 'Selected (click to deselect)' : 'Select'}
      </Box>
    </Box>
  );
}
